import React, { useState, useEffect } from "react";
import { NameCard } from "./NameCard";
import completedIcon from "../../../assets/shapes_svg/ambition/actionplans/complete-progress.svg";
import noProgressIcon from "../../../assets/shapes_svg/ambition/actionplans/no-progress.svg";
import inProgressIcon from "../../../assets/shapes_svg/ambition/actionplans/in-progress.svg";
import {
  getEffortSuccessCriteria,
  createEffortSuccessCriteria,
  updateEffortSuccessCriteria,
} from "../../../apis/Business/SuccessCriteriaApi";
import { useUserAccess } from "../../../components/WriteAccessContext";

const ProgressIcons = {
  completed: completedIcon,
  "on-progress": inProgressIcon,
  "not-started": noProgressIcon,
};

const SuccessCriteria = ({ year, effortId }) => {
  const units = [
    "Kg",
    "lbs",
    "meter",
    "feet",
    "km",
    "tons",
    "grams",
    "m2",
    "m3",
    "tCO2e",
    "Ltr",
    "Mj",
    "FEe",
    "%",
  ];
  const [selectedYear, setSelectedYear] = useState(year);
  const [criteriaByYear, setCriteriaByYear] = useState({});
  const [goalYear, setGoalYear] = useState(2030);
  const [editingGoalYear, setEditingGoalYear] = useState(null);
  const { userAccess } = useUserAccess();

  useEffect(() => {
    if (effortId) {
      const fetchSuccessCriteria = async () => {
        try {
          const data = await getEffortSuccessCriteria(effortId);
          const groupedData = data.reduce((acc, item) => {
            const yearKey = item.year;
            if (!acc[yearKey]) acc[yearKey] = [];
            acc[yearKey].push(item);
            return acc;
          }, {});
          setCriteriaByYear(groupedData);
        } catch (error) {
          console.error("Error fetching success criteria:", error);
        }
      };
      fetchSuccessCriteria();
    }
  }, [effortId]);

  const handleUnitChange = async (index, newUnit) => {
    const updatedCriteria = [...criteriaByYear[selectedYear]];
    const criteriaToUpdate = updatedCriteria[index];

    try {
      const updatedItem = await updateEffortSuccessCriteria(
        effortId,
        criteriaToUpdate.id,
        { ...criteriaToUpdate, unit: newUnit }
      );

      setCriteriaByYear((prev) => ({
        ...prev,
        [selectedYear]: prev[selectedYear].map((item, i) =>
          i === index ? { ...item, unit: newUnit } : item
        ),
      }));
    } catch (error) {
      console.error("Error updating unit:", error);
    }
  };

  const handleProgressChange = async (index, newProgress) => {
    const updatedCriteria = [...criteriaByYear[selectedYear]];
    const criteriaToUpdate = updatedCriteria[index];

    try {
      const updatedItem = await updateEffortSuccessCriteria(
        effortId,
        criteriaToUpdate.id,
        { ...criteriaToUpdate, progress: newProgress }
      );

      setCriteriaByYear((prev) => ({
        ...prev,
        [selectedYear]: prev[selectedYear].map((item, i) =>
          i === index ? { ...item, progress: newProgress } : item
        ),
      }));
    } catch (error) {
      console.error("Error updating progress:", error);
    }
  };

  const handleGoalYearUpdate = async (newGoalYear) => {
    try {
      // Update all items for the selected year in the backend
      const updatedCriteria = await Promise.all(
        (criteriaByYear[selectedYear] || []).map(async (item) => {
          const updatedItem = await updateEffortSuccessCriteria(
            effortId,
            item.id,
            { ...item, goal_year: newGoalYear.toString() }
          );
          return updatedItem;
        })
      );

      // Update the state with the new goal year
      setCriteriaByYear((prev) => ({
        ...prev,
        [selectedYear]: (prev[selectedYear] || []).map((item) => {
          const updatedItem = updatedCriteria.find(
            (updated) => updated.id === item.id
          );
          return updatedItem || item;
        }),
      }));

      setGoalYear(newGoalYear); // Update the header value
      setEditingGoalYear(false); // Exit editing mode
    } catch (error) {
      console.error("Error updating goal year:", error);
    }
  };
  const [newCriteriaData, setNewCriteriaData] = useState({
    success_criteria: "",
    qty: "",
    goal_year: "2030",
    actual_qty: "",
    progress: "not-started",
    unit: "Quantity",
  });

  const handleNewCriteriaChange = (field, value) => {
    setNewCriteriaData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const addCriteria = async () => {
    if (newCriteriaData.success_criteria.trim()) {
      try {
        const newItem = await createEffortSuccessCriteria(effortId, {
          ...newCriteriaData,
          year: selectedYear.toString(),
          qty: parseFloat(newCriteriaData.qty) || 0,
          actual_qty: parseFloat(newCriteriaData.actual_qty) || null,
        });

        setCriteriaByYear((prev) => ({
          ...prev,
          [selectedYear]: [...(prev[selectedYear] || []), newItem],
        }));

        // Reset form
        setNewCriteriaData({
          success_criteria: "",
          qty: "",
          actual_qty: "",
          progress: "not-started",
          unit: "Quantity",
        });
      } catch (error) {
        console.error("Error adding criteria:", error);
      }
    }
  };

  return (
    <div
      style={{
        padding: "20px 20px 20px 0",
        display: "flex",
        gap: "20px",
      }}
    >
      <NameCard
        deptName={"Success Criteria (End of Effort)"}
        isBottom={false}
        className={"other-effort-container"}
      />
      <div className="success-criteria-container action-plans-parent-bubble">
        <style>
          {`
            .success-criteria-container {
              width: 100%;
            }

            .criteria-table {
              width: 100%;
              border-collapse: separate;
              border-spacing: 5px;
            }

            .criteria-table th,
            .criteria-table td {
              padding: 9px;
              text-align: center;
              margin-right: 5px;
            }

            .criteria-table th {
              clip-path: polygon( 99.664% 11.066%,99.664% 11.066%,99.585% 37.136%,99.717% 57.557%,99.835% 73.017%,99.715% 84.201%,99.132% 91.794%,97.862% 96.483%,95.679% 98.953%,92.359% 99.889%,87.677% 99.977%,81.409% 99.904%,81.409% 99.904%,73.766% 99.904%,64.973% 99.904%,55.481% 99.904%,45.741% 99.904%,36.203% 99.904%,27.318% 99.904%,19.535% 99.904%,13.306% 99.904%,9.081% 99.904%,7.309% 99.904%,7.309% 99.904%,7.326% 99.887%,7.318% 99.883%,7.281% 99.887%,7.211% 99.893%,7.103% 99.895%,6.952% 99.888%,6.755% 99.867%,6.506% 99.824%,6.201% 99.756%,5.836% 99.656%,5.836% 99.656%,3.495% 98.11%,1.885% 95.452%,0.884% 91.77%,0.367% 87.154%,0.211% 81.692%,0.294% 75.472%,0.49% 68.584%,0.678% 61.115%,0.734% 53.154%,0.534% 44.791%,0.534% 44.791%,0.539% 36.03%,0.474% 28.29%,0.428% 21.546%,0.486% 15.777%,0.736% 10.958%,1.265% 7.065%,2.159% 4.076%,3.506% 1.967%,5.392% 0.713%,7.905% 0.293%,7.905% 0.293%,32.01% 0.426%,51.415% 0.305%,66.638% 0.105%,78.198% 0%,86.613% 0.162%,92.402% 0.766%,96.083% 1.985%,98.175% 3.992%,99.196% 6.961%,99.664% 11.066% );
              background-color: #1B3764;
              padding: 9px;
              color: white;
              font-weight: bold;
              white-space: nowrap;
            }

            .criteria-table tr {
              background-color: transparent;
              margin-bottom: 10px;
            }

            .criteria-table tr:last-child {
              background-color: #f5f5f5;
            }

            .text-input {
              padding: 8px 12px;
              border: none;
              font-size: 14px;
              width: 100%;
              color: #1B3764;
              background-color: transparent;
              outline: none;
              text-align: center;
              white-space: pre-wrap;
              hyphens: auto;
              word-wrap: break-word;
              overflow-wrap: break-word;
            }

            .text-input::placeholder {
              color: #1B3764;
            }

            .select-input {
              padding: 8px 12px;
              border: none;
              font-size: 14px;
              cursor: pointer;
              min-width: 100px;
              clip-path: polygon( 98.985% 11.617%,98.985% 11.617%,98.906% 37.413%,99.037% 57.621%,99.155% 72.919%,99.036% 83.986%,98.456% 91.5%,97.191% 96.14%,95.018% 98.583%,91.714% 99.51%,87.055% 99.597%,80.816% 99.525%,80.816% 99.525%,73.209% 99.525%,64.457% 99.525%,55.01% 99.525%,45.316% 99.525%,35.823% 99.525%,26.979% 99.525%,19.234% 99.525%,13.034% 99.525%,8.828% 99.525%,7.065% 99.525%,7.065% 99.525%,7.082% 99.508%,7.074% 99.504%,7.037% 99.508%,6.967% 99.514%,6.859% 99.516%,6.71% 99.509%,6.513% 99.488%,6.266% 99.446%,5.963% 99.378%,5.599% 99.279%,5.599% 99.279%,3.269% 97.749%,1.667% 95.119%,0.67% 91.476%,0.155% 86.908%,0.001% 81.503%,0.082% 75.349%,0.278% 68.532%,0.465% 61.141%,0.521% 53.264%,0.321% 44.988%,0.321% 44.988%,0.326% 36.319%,0.263% 28.66%,0.216% 21.987%,0.274% 16.278%,0.523% 11.51%,1.049% 7.658%,1.939% 4.7%,3.28% 2.612%,5.157% 1.372%,7.658% 0.956%,7.658% 0.956%,31.65% 1.087%,50.963% 0.968%,66.114% 0.771%,77.62% 0.666%,85.995% 0.827%,91.757% 1.424%,95.421% 2.63%,97.503% 4.617%,98.519% 7.555%,98.985% 11.617% );
              background-color: rgba(27, 55, 100, 0.3);
              color: #1B3764;
            }

            .nav-button {
              background: transparent;
              border: none;
              color: white;
              cursor: pointer;
              font-size: 16px;
              padding: 4px 8px;
            }

            .nav-button:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }

            .add-button {
              padding: 9px 12px;
               clip-path: polygon( 99.826% 11.082%,99.826% 11.082%,99.746% 36.779%,99.879% 56.909%,99.997% 72.147%,99.877% 83.171%,99.292% 90.656%,98.016% 95.278%,95.825% 97.712%,92.493% 98.635%,87.794% 98.722%,81.503% 98.65%,81.503% 98.65%,73.83% 98.65%,65.005% 98.65%,55.478% 98.65%,45.701% 98.65%,36.127% 98.65%,27.209% 98.65%,19.397% 98.65%,13.144% 98.65%,8.903% 98.65%,7.125% 98.65%,7.125% 98.65%,7.142% 98.633%,7.134% 98.629%,7.097% 98.633%,7.026% 98.638%,6.918% 98.641%,6.767% 98.634%,6.569% 98.613%,6.319% 98.571%,6.013% 98.504%,5.647% 98.405%,5.647% 98.405%,3.297% 96.881%,1.681% 94.261%,0.675% 90.633%,0.157% 86.082%,0.001% 80.698%,0.083% 74.567%,0.281% 67.777%,0.469% 60.415%,0.525% 52.568%,0.324% 44.325%,0.324% 44.325%,0.329% 35.689%,0.265% 28.059%,0.218% 21.413%,0.277% 15.726%,0.528% 10.976%,1.058% 7.139%,1.956% 4.192%,3.308% 2.113%,5.201% 0.878%,7.723% 0.464%,7.723% 0.464%,31.918% 0.594%,51.396% 0.475%,66.676% 0.279%,78.279% 0.175%,86.726% 0.335%,92.536% 0.93%,96.231% 2.131%,98.331% 4.109%,99.356% 7.036%,99.826% 11.082% );
              background-color: #1a365d;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-size: 14px;
              width: 100%;
            }
            .criteria-table .first-input-td {
            clip-path: polygon( 99.826% 11.082%,99.826% 11.082%,99.746% 36.779%,99.879% 56.909%,99.997% 72.147%,99.877% 83.171%,99.292% 90.656%,98.016% 95.278%,95.825% 97.712%,92.493% 98.635%,87.794% 98.722%,81.503% 98.65%,81.503% 98.65%,73.83% 98.65%,65.005% 98.65%,55.478% 98.65%,45.701% 98.65%,36.127% 98.65%,27.209% 98.65%,19.397% 98.65%,13.144% 98.65%,8.903% 98.65%,7.125% 98.65%,7.125% 98.65%,7.142% 98.633%,7.134% 98.629%,7.097% 98.633%,7.026% 98.638%,6.918% 98.641%,6.767% 98.634%,6.569% 98.613%,6.319% 98.571%,6.013% 98.504%,5.647% 98.405%,5.647% 98.405%,3.297% 96.881%,1.681% 94.261%,0.675% 90.633%,0.157% 86.082%,0.001% 80.698%,0.083% 74.567%,0.281% 67.777%,0.469% 60.415%,0.525% 52.568%,0.324% 44.325%,0.324% 44.325%,0.329% 35.689%,0.265% 28.059%,0.218% 21.413%,0.277% 15.726%,0.528% 10.976%,1.058% 7.139%,1.956% 4.192%,3.308% 2.113%,5.201% 0.878%,7.723% 0.464%,7.723% 0.464%,31.918% 0.594%,51.396% 0.475%,66.676% 0.279%,78.279% 0.175%,86.726% 0.335%,92.536% 0.93%,96.231% 2.131%,98.331% 4.109%,99.356% 7.036%,99.826% 11.082% );
            background-color: rgba(255, 255, 255, 0.6);
            }

            .criteria-table .diagonal {
            clip-path: polygon( 63.882% 1.324%,63.882% 1.324%,50.882% 0.826%,40.494% 1.131%,32.29% 2.354%,25.843% 4.61%,20.725% 8.012%,16.509% 12.675%,12.766% 18.714%,9.071% 26.242%,4.995% 35.375%,0.11% 46.226%,0.11% 46.226%,0.286% 52.267%,1.836% 59.131%,4.529% 66.447%,8.135% 73.843%,12.423% 80.947%,17.163% 87.389%,22.122% 92.795%,27.072% 96.795%,31.779% 99.016%,36.015% 99.087%,36.015% 99.087%,49.16% 99.646%,59.661% 99.258%,67.944% 97.856%,74.433% 95.376%,79.554% 91.752%,83.732% 86.919%,87.393% 80.811%,90.962% 73.363%,94.864% 64.509%,99.524% 54.184%,99.524% 54.184%,99.432% 48.433%,97.954% 41.746%,95.321% 34.513%,91.762% 27.124%,87.509% 19.968%,82.79% 13.435%,77.838% 7.915%,72.883% 3.796%,68.154% 1.47%,63.882% 1.324% );
            background-color: rgba(26, 56, 100, 0.8);
            color: white;
            }
            .diagonal::placeholder {
            color: white;
            }
          `}
        </style>

        <table className="criteria-table">
          <thead>
            <tr>
              <th>Title of Criteria</th>
              <th>
                {editingGoalYear && userAccess === "write" ? (
                  <input
                    type="text"
                    value={goalYear}
                    onChange={(e) => setGoalYear(parseInt(e.target.value, 10))}
                    onBlur={() => handleGoalYearUpdate(goalYear)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") handleGoalYearUpdate(goalYear);
                    }}
                    className="text-input"
                  />
                ) : (
                  <div onClick={() => setEditingGoalYear(true)}>{goalYear}</div>
                )}
              </th>
              <th>Unit</th>

              <th>
                <button
                  className="nav-button"
                  onClick={() =>
                    setSelectedYear((prev) =>
                      Math.max(Math.min(prev - 1, 2030), year)
                    )
                  }
                  disabled={selectedYear <= year}
                >
                  ←
                </button>
              </th>
              <th>{selectedYear}</th>
              <th>Progress</th>
              <th>
                <button
                  className="nav-button"
                  onClick={() =>
                    setSelectedYear((prev) =>
                      Math.max(Math.min(prev + 1, 2030), year)
                    )
                  }
                  disabled={selectedYear >= 2030}
                >
                  →
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {(criteriaByYear[selectedYear] || []).map((item, index) => (
              <tr key={index}>
                <td className="first-input-td">
                  <input
                    type="text"
                    value={item.success_criteria}
                    onChange={(e) => {
                      setCriteriaByYear((prev) => ({
                        ...prev,
                        [selectedYear]: prev[selectedYear].map((crit, i) =>
                          i === index
                            ? { ...crit, success_criteria: e.target.value }
                            : crit
                        ),
                      }));
                    }}
                    disabled={userAccess !== "write"}
                    onBlur={async () => {
                      if (userAccess === "write") {
                        try {
                          await updateEffortSuccessCriteria(effortId, item.id, {
                            ...item,
                            success_criteria: item.success_criteria,
                          });
                        } catch (error) {
                          console.error(
                            "Error updating success criteria:",
                            error
                          );
                        }
                      }
                    }}
                    className="text-input"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={item.qty}
                    disabled={userAccess !== "write"}
                    onChange={(e) => {
                      setCriteriaByYear((prev) => ({
                        ...prev,
                        [selectedYear]: prev[selectedYear].map((crit, i) =>
                          i === index ? { ...crit, qty: e.target.value } : crit
                        ),
                      }));
                    }}
                    onBlur={async () => {
                      if (userAccess === "write") {
                        try {
                          await updateEffortSuccessCriteria(effortId, item.id, {
                            ...item,
                            qty: item.qty,
                          });
                        } catch (error) {
                          console.error("Error updating quantity:", error);
                        }
                      }
                    }}
                    className="text-input diagonal"
                  />
                </td>
                <td>
                  <select
                    className="select-input"
                    value={item.unit}
                    onChange={(e) => {
                      if (userAccess === "write") {
                        handleUnitChange(index, e.target.value);
                      }
                    }}
                    disabled={userAccess !== "write"}
                  >
                    {units.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </td>
                <td></td>

                <td>
                  <input
                    type="number"
                    value={item.actual_qty || ""}
                    disabled={userAccess !== "write"}
                    onChange={(e) => {
                      setCriteriaByYear((prev) => ({
                        ...prev,
                        [selectedYear]: prev[selectedYear].map((crit, i) =>
                          i === index
                            ? { ...crit, actual_qty: e.target.value }
                            : crit
                        ),
                      }));
                    }}
                    onBlur={async () => {
                      if (userAccess === "write") {
                        try {
                          await updateEffortSuccessCriteria(effortId, item.id, {
                            ...item,
                            actual_qty: item.actual_qty,
                          });
                        } catch (error) {
                          console.error(
                            "Error updating actual quantity:",
                            error
                          );
                        }
                      }
                    }}
                    className="text-input"
                  />
                </td>
                <td>
                  <div
                    className="select-input"
                    onClick={() => {
                      if (userAccess === "write") {
                        const statuses = Object.keys(ProgressIcons);
                        const currentIndex = statuses.indexOf(item.progress);
                        const nextIndex = (currentIndex + 1) % statuses.length;
                        handleProgressChange(index, statuses[nextIndex]);
                      }
                    }}
                    style={{
                      cursor: userAccess === "write" ? "pointer" : "default",
                    }}
                  >
                    <img
                      src={ProgressIcons[item.progress]}
                      alt={`${item.progress} icon`}
                      width={25}
                      height={25}
                    />
                  </div>
                </td>
                <td></td>
              </tr>
            ))}
            <tr>
              <td className="first-input-td">
                <textarea
                  disabled={userAccess !== "write"}
                  placeholder="Type Success criteria here"
                  value={newCriteriaData.success_criteria}
                  onChange={(e) =>
                    handleNewCriteriaChange("success_criteria", e.target.value)
                  }
                  className="text-input"
                />
              </td>
              <td>
                <input
                  type="text"
                  disabled={userAccess !== "write"}
                  placeholder="Qty"
                  value={newCriteriaData.qty}
                  onChange={(e) =>
                    handleNewCriteriaChange("qty", e.target.value)
                  }
                  className="text-input diagonal"
                />
              </td>
              <td>
                <select
                  value={newCriteriaData.unit}
                  disabled={userAccess !== "write"}
                  onChange={(e) =>
                    handleNewCriteriaChange("unit", e.target.value)
                  }
                  className="select-input"
                >
                  {units.map((unit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
              </td>
              <td></td>

              <td>
                <input
                  type="text"
                  placeholder="Actual Value"
                  disabled={userAccess !== "write"}
                  value={newCriteriaData.actual_qty}
                  onChange={(e) =>
                    handleNewCriteriaChange("actual_qty", e.target.value)
                  }
                  className="text-input"
                />
              </td>
              <td>
                <div
                  className="select-input"
                  onClick={() => {
                    const statuses = Object.keys(ProgressIcons);
                    const currentIndex = statuses.indexOf(
                      newCriteriaData.progress
                    );
                    const nextIndex = (currentIndex + 1) % statuses.length;
                    handleNewCriteriaChange("progress", statuses[nextIndex]);
                  }}
                >
                  <img
                    src={ProgressIcons[newCriteriaData.progress]}
                    alt={`${newCriteriaData.progress} icon`}
                    width={25}
                    height={25}
                  />
                </div>
              </td>
              <td></td>
            </tr>
          </tbody>

          <button
            onClick={userAccess === "write" ? addCriteria : undefined}
            className="add-button"
            style={{ marginTop: "20px" }}
            disabled={userAccess !== "write"}
          >
            + Add Criteria
          </button>
        </table>
      </div>
    </div>
  );
};

export default SuccessCriteria;
